import React, { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { renderButtonRowBlock } from "../render/blocks/button-row";
import {
  buttonChromeLinkStyle,
} from "../domain/blocks/button-presentation";
import { resolveButtonChrome } from "../domain/button-theme";
import {
  buttonRowExpandsHorizontally,
  buttonRowSlotFillsHorizontally,
} from "../domain/button-row-layout";
import { selectionEquals } from "../domain/selection/equals";
import { isTextBlockEditing } from "../domain/text-editing";
import {
  selectEmailSettings,
  selectSelection,
  selectTextEditingBlock,
} from "../domain/selectors";
import { useEditorActions, useEditorStore, getEditorStoreState } from "../domain/store";
import { shouldDeferChildPointerActivation } from "../domain/selection/targets";
import type { ButtonRowBlock, ButtonRowSlot, Selection } from "../domain/types";
import { isEmailBuilderRichTextEmpty } from "../editing/richText/emailBuilderRichTextHtml";
import { EmailBuilderRichTextEditor } from "../editing/richText/EmailBuilderRichTextEditor";
import { MERGE_TAG_KEYS } from "../domain/merge-tags";
import { useEditorFacility } from "./EditorFacilityContext";
import { registerFormattingRailToolbarPress } from "./formattingRailToolbarPress";
import {
  useEditableButtonRowSlotEditSession,
  useEditableButtonRowSlotPointer,
} from "./useEditableButtonRowSlot";

type EditorButtonRowProps = {
  rowId: string;
  columnId: string;
  block: ButtonRowBlock;
  hover: Selection | null;
  setHover: (selection: Selection | null) => void;
};

function buttonRowSlotEditorId(blockId: string): string {
  return blockId;
}

function EditableButtonRowSlotLabel({
  block,
  slot,
}: {
  block: ButtonRowBlock;
  slot: ButtonRowSlot;
}) {
  const textTheme = useEditorStore(selectEmailSettings);
  const chrome = resolveButtonChrome(slot, textTheme);
  const labelStyle = buttonChromeLinkStyle({ ...slot, ...chrome }, textTheme.bodyFontFamily);
  const editorId = buttonRowSlotEditorId(block.id);

  return (
    <EmailBuilderRichTextEditor
      blockId={editorId}
      kind="label"
      content={slot.label}
      fontWeight={slot.fontWeight}
      editorClassName="email-builder-inline-button-label"
      editorStyle={labelStyle}
      ariaLabel={slot.label}
      placeholder=""
      mergeTags={MERGE_TAG_KEYS}
      contentAttribute="data-email-builder-inline-button-label"
    />
  );
}

function EditableButtonRowSlot({
  rowId,
  columnId,
  block,
  slotIndex,
  hover,
  setHover,
  children,
}: {
  rowId: string;
  columnId: string;
  block: ButtonRowBlock;
  slotIndex: number;
  hover: Selection | null;
  setHover: (selection: Selection | null) => void;
  children: React.ReactNode;
}) {
  const { t } = useTranslation();
  const selection = useEditorStore(selectSelection);
  const textEditingBlock = useEditorStore(selectTextEditingBlock);
  const { setSelection, setTextEditingBlock, setTextEditReentryGuard, updateBlock } =
    useEditorActions();
  const rootRef = useRef<HTMLDivElement>(null);
  const exitingRef = useRef(false);
  const toolbarPressRef = useRef(false);
  const editorId = buttonRowSlotEditorId(block.id);
  useEffect(
    () => registerFormattingRailToolbarPress(editorId, toolbarPressRef),
    [editorId],
  );

  const slotSelection: Selection = {
    kind: "buttonRowSlot",
    rowId,
    columnId,
    blockId: block.id,
    slotIndex,
  };
  const selected = selectionEquals(selection, slotSelection);
  const isEditing = isTextBlockEditing(
    textEditingBlock,
    rowId,
    columnId,
    block.id,
    slotIndex,
  );
  const fill = buttonRowSlotFillsHorizontally(block, slotIndex);
  const slotHovered = hover ? selectionEquals(hover, slotSelection) : false;

  const { enterEdit } = useEditableButtonRowSlotEditSession({
    rowId,
    columnId,
    block,
    slotIndex,
    editorId,
    isEditing,
    setSelection,
    setTextEditingBlock,
    updateBlock,
    setTextEditReentryGuard,
    rootRef,
    exitingRef,
    toolbarPressRef,
  });

  const { rootHandlers } = useEditableButtonRowSlotPointer({
    editorId,
    isEditing,
    selected,
    setSelection,
    setTextEditReentryGuard,
    slotSelection,
    enterEdit,
  });

  return (
    <div
      ref={rootRef}
      role="button"
      tabIndex={selected ? 0 : -1}
      className="email-builder-canvas-slot-frame"
      data-entity-id={`button-row-slot:${block.id}:${slotIndex}`}
      data-slot-index={slotIndex}
      data-fill={fill ? "true" : "false"}
      data-selected={selected ? "true" : "false"}
      data-hovered={!selected && slotHovered ? "true" : "false"}
      data-button-editing={isEditing ? "true" : undefined}
      aria-label={t("Button {{number}}", { number: slotIndex + 1 })}
      onPointerMove={() => setHover(slotSelection)}
      {...rootHandlers}>
      {children}
    </div>
  );
}

export function EditorButtonRow({
  rowId,
  columnId,
  block,
  hover,
  setHover,
}: EditorButtonRowProps) {
  const { t } = useTranslation();
  const facility = useEditorFacility();
  const textTheme = useEditorStore(selectEmailSettings);
  const textEditingBlock = useEditorStore(selectTextEditingBlock);
  const { setSelection } = useEditorActions();

  const blockSelection: Selection = {
    kind: "block",
    rowId,
    columnId,
    blockId: block.id,
  };

  function handleRowPointerMove(event: React.MouseEvent<HTMLDivElement>) {
    const target = event.target;
    if (!(target instanceof Element)) {
      return;
    }

    if (target.closest("[data-slot-index]")) {
      return;
    }

    setHover(blockSelection);
  }

  const slotLabelSlots = block.buttons.map((slot, index) => {
    const isEditing = isTextBlockEditing(
      textEditingBlock,
      rowId,
      columnId,
      block.id,
      index,
    );
    const isEmpty = isEmailBuilderRichTextEmpty(slot.label);
    const chrome = resolveButtonChrome(slot, textTheme);
    const labelStyle = buttonChromeLinkStyle(
      { ...slot, ...chrome },
      textTheme.bodyFontFamily,
    );

    if (isEditing) {
      return (
        <EditableButtonRowSlotLabel
          key={index}
          block={block}
          slot={slot}
        />
      );
    }

    if (isEmpty) {
      return (
        <span
          key={index}
          className="email-builder-canvas-block-placeholder"
          style={labelStyle}
          aria-hidden="true">
          {t("Button")}
        </span>
      );
    }

    return undefined;
  }) as [React.ReactNode?, React.ReactNode?];

  return (
    <div
      className="email-builder-canvas-button-row"
      data-expands={buttonRowExpandsHorizontally(block) ? "true" : "false"}
      onMouseMove={handleRowPointerMove}
      onPointerDown={event => {
        if (event.button !== 0) {
          return;
        }
        if ((event.target as Element).closest?.("[data-slot-index]")) {
          return;
        }
        const { selection, document } = getEditorStoreState();
        if (
          shouldDeferChildPointerActivation(
            selection,
            blockSelection,
            document.sections,
            event,
          )
        ) {
          return;
        }
        event.stopPropagation();
        setSelection(blockSelection);
        setHover(blockSelection);
      }}>
      {renderButtonRowBlock({
        block,
        facility,
        textTheme,
        mergeMode: "raw",
        editorMode: true,
        slotLabelSlots,
        wrapSlot: (index, node) => (
          <EditableButtonRowSlot
            rowId={rowId}
            columnId={columnId}
            block={block}
            slotIndex={index}
            hover={hover}
            setHover={setHover}>
            {node}
          </EditableButtonRowSlot>
        ),
      })}
    </div>
  );
}
